import { limit, orderBy, where } from "firebase/firestore";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import AnalyticsDatePickerModal from "../../../components/modals/analyticsDatePicker/analyticsDatePickerModal";
import BulkReassignModal from "../../../components/modals/bulkReassignModal/bulkReassignModal";
import DocViewerWithSideNavigation from "../../../components/modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import ImageviewerModal from "../../../components/modals/imageViewerModal/imageviewerModal";
import OrderProcessingInfo from "../../../components/modals/OrderProcessingInfo/OrderProcessingInfo";
import ViewReviewModal from "../../../components/modals/ViewReviewModal/ViewReviewModal";
import { EXPORT_DATA_LIMIT } from "../../../constants/medicalPAbatch";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../enums/modalId";
import {
  closeModal as closeReduxModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../../store/UserGeneralInfoStore";
import {
  exportDataFromTableForMedicalPaOrders,
  exportDataToExcel,
} from "../../../utils/exportDataToExcel";
import { OrderTrailOrderContext } from "../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import WorklistTasksNotificationTrigger from "../components/worklistTasksNotificationTrigger";
import OrderAssignmentInfo from "../medicalPaOrders/components/orderAssignmentInfo";
import { handleReassignInBatchMode } from "../medicalPaOrders/utils/medicalPaOrdersHandlers";
import ClientExportButton from "./components/clientExportButton";
import MedicalPaOrdersTable from "./components/medicalPaOrdersTable";
import { useModalOpenerForMedicalPaOrders } from "./hooks/useModalOpenerForMedicalPaOrders";
import {
  fetchProcessedOrdersData,
  ProcessedOrdersData,
} from "./utils/fetchProcessedOrdersData";
import { handleOrdersProcessingInfoClick } from "./utils/handleOrdersProcessingInfoClick";

import { useLocation } from "react-router-dom";
import DocumentScreenshotViewerModal from "../../../components/modals/documentScreenshotViewerModal/documentScreenshotViewerModal";
import { DOCUMENT_SCREENSHOT_VIEWER_TITLE } from "../../../constants/stringLiterals";
import { useOncoemrTasksEnabled } from "../../../hooks/useOncoemrTasksEnabled";
import { useAssignedToUpdate } from "./hooks/useAssignedToUpdate";
/** Props for the MedicalPaOrders page component. */
interface MedicalPaOrdersProps {
  /** When true, signals a completed bulk operation that should trigger a table refresh. */
  onComplete?: boolean;
}

/** Default page title for the medical PA orders worklist. */
const DEFAULT_PAGE_TITLE = "All PA Orders";

/**
 * URL slug → page title overrides. The page title is selected by checking
 * if `pathname.endsWith(slug)`. The same `/orders-to-process` predicate is
 * used by `useDefaultFacetFiltersForRoute`, which scopes every fetch on
 * that worklist to `master_auth_status: orders_to_process`.
 */
const ROUTE_SUFFIX_TO_PAGE_TITLE: ReadonlyArray<{
  routeSuffix: string;
  title: string;
}> = [{ routeSuffix: "/orders-to-process", title: "Orders To Process" }];

/**
 * Top-level page component for the medical PA orders worklist.
 * Coordinates the orders table, bulk reassignment modal, data export,
 * and various viewer modals (image, document, write-back trail, review).
 *
 * The page title shown in the header is derived from the current URL
 * via `ROUTE_SUFFIX_TO_PAGE_TITLE` (e.g. `/orders-to-process` →
 * "Orders To Process"), so the same component can drive both the
 * default "All PA Orders" view and any route-specific variants.
 */
const MedicalPaOrders = (props: MedicalPaOrdersProps) => {
  const isOncoemrTasksEnabled = useOncoemrTasksEnabled();
  const medicalPaOrdersTableRef = useRef<HTMLDivElement>(null);
  const [selectedOrders, setSelectedOrders] = useState<
    OrderTrailOrderContext[]
  >([]);
  const [showLoading, setShowLoading] = useState(false);
  const [, setProcessedOrdersToday] = useState<ProcessedOrdersData>({
    completion_percentage: 0,
    orders_processed: 0,
    orders_remaining: 0,
    time_taken: "",
    loading: false,
    skipped_duplicate_order_ids: [],
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const { data: providersDetails } = useSelector(
    (state: RootState) => state.allProviders,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: healthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const { openedModalId, metaData } = useModalOpenerForMedicalPaOrders();
  const dispatch = useDispatch<AppDispatch>();
  const [isUpdating, setIsUpdating] = useState(false);
  const [clientExportPayload, setClientExportPayload] =
    useState<SearchForMedicalPaOrdersFromPostgresParams | null>(null);
  const { runUpdate, abort } = useAssignedToUpdate<{}>(true);
  const { pathname } = useLocation();
  const pageTitle = useMemo(() => {
    const match = ROUTE_SUFFIX_TO_PAGE_TITLE.find(({ routeSuffix }) =>
      pathname.endsWith(routeSuffix),
    );
    return match?.title ?? DEFAULT_PAGE_TITLE;
  }, [pathname]);

  useEffect(() => {
    fetchProcessedOrdersToday();
    UserStore.setItem(
      UserGeneralInfoContextKeys.PARENT_TABLE,
      window.location.pathname.split("/").slice(-1)[0],
    );
  }, []);

  /**
   * Fetches medical PA orders within a date range from Firestore and exports them to Excel.
   *
   * @param startDate - Start of the export date range
   * @param endDate - End of the export date range
   */
  const handleDataFetchingForSelectedDateRange = async (
    startDate: Date,
    endDate: Date,
  ) => {
    try {
      setShowLoading(true);
      const startDateForExport = moment(startDate)
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      const endOfDayendDateForExport = moment(endDate)
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      const data = await FirestoreService.getDocumentsByQuery(
        FirestoreCollectionReference.medicalPaOrders(),
        [
          where("created_at", ">=", startDateForExport),
          where("created_at", "<=", endOfDayendDateForExport),
          where(
            "assigned_to.facility_id",
            "==",
            getOrgIdForFetchExternalWorklist(),
          ),
          orderBy("created_at", "desc"),
          limit(EXPORT_DATA_LIMIT),
        ],
      );
      const exportedData = exportDataFromTableForMedicalPaOrders(
        data as MedicalPaOrder[],
      );
      exportDataToExcel(
        exportedData,
        `medicalPaOrders-${moment().format("YYYY-MM-DD")}`,
      );
    } catch (error) {
    } finally {
      setShowLoading(false);
    }
  };

  const fetchProcessedOrdersToday = async () => {
    try {
      setProcessedOrdersToday((prev) => ({ ...prev, loading: true }));
      const data = await fetchProcessedOrdersData();
      setProcessedOrdersToday(data);
    } catch (error) {
      setProcessedOrdersToday({
        completion_percentage: 0,
        orders_processed: 0,
        orders_remaining: 0,
        time_taken: "",
        loading: false,
        skipped_duplicate_order_ids: [],
      });
    }
  };

  const handleDataChange = (data: OrderTrailOrderContext[]) => {
    setSelectedOrders(data);
  };

  return (
    <div className="medical-pa-orders--main-container flex h-full flex-col overflow-hidden pb-1">
      <div className="medical-pa-orders__header mb-4 flex items-center justify-between text-h11 font-bold text-primaryGray-1">
        {pageTitle}
        <div className="flex cursor-pointer items-center gap-2 rounded-md bg-primaryGray-16 p-2 text-tiny font-normal text-primaryGray-1 transition-all duration-300 ease-in">
          <div className="orders-assignemnt-information border-r-2 border-primaryGray-7 hover:font-bold">
            <OrderAssignmentInfo />
          </div>
          <div
            className="view-orders-processing-information hover:font-bold"
            onClick={() =>
              handleOrdersProcessingInfoClick(
                dispatch,
                healthFacilityDetails?.global_route,
              )
            }
          >
            View Orders Processing Infomation
          </div>
          {isOncoemrTasksEnabled && <WorklistTasksNotificationTrigger />}
          <ClientExportButton payload={clientExportPayload} />
        </div>
      </div>

      <div className="medical-pa-orders__table-container flex-1 overflow-auto">
        <MedicalPaOrdersTable
          medicalPaOrdersTableRef={medicalPaOrdersTableRef}
          onDataChange={handleDataChange}
          onComplete={isSuccess}
          onBulkAssignButtonClick={() => {
            dispatch(setOpenedModalId({ id: ModalId.BULK_REASSIGN_MODAL }));
          }}
          exportingInProgress={showLoading}
          onFilterPayloadChange={setClientExportPayload}
        />
      </div>
      {openedModalId === ModalId.BULK_REASSIGN_MODAL && (
        <BulkReassignModal
          isLoading={isUpdating}
          onClose={() => {
            dispatch(closeReduxModal());
            abort();
            setIsUpdating(false);
            setIsSuccess(false);
          }}
          onSave={(selectedProvider) =>
            handleReassignInBatchMode(
              selectedProvider,
              selectedOrders,
              providersDetails ?? undefined,
              {
                runUpdate,
                setIsUpdating,
                setIsSuccess,
                dispatch,
                user,
                orgId: orgId ?? "",
                providerEmail: user?.email ?? "",
                providerName:
                  `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
                  "Unknown Provider",
              },
            )
          }
          shouldLogEvent={true}
        />
      )}

      <>
        <>
          {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
            <ImageviewerModal
              fileUrls={metaData?.fileUrls ?? []}
              filePaths={metaData?.filePaths ?? []}
              onClose={() => dispatch(closeReduxModal())}
              showLoader={metaData?.showLoader ?? false}
            />
          )}
          {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
            <DocViewerWithSideNavigation />
          )}
        </>
        {openedModalId === ModalId.ANALYTICS_DATE_PICKER_MODAL && (
          <AnalyticsDatePickerModal
            onClose={() => dispatch(closeReduxModal())}
            onDateRangeSelect={(startDate, endDate) => {
              handleDataFetchingForSelectedDateRange(startDate, endDate);
            }}
            maxRecords={EXPORT_DATA_LIMIT}
          />
        )}
        {openedModalId === ModalId.ORDER_PROCESSING_INFO_MODAL && (
          <OrderProcessingInfo />
        )}
        {openedModalId === ModalId.VIEW_REVIEW_MODAL && (
          <ViewReviewModal
            onClose={() => dispatch(closeReduxModal())}
            reviewData={metaData?.reviewData ?? null}
            isLoading={metaData?.isLoading ?? false}
          />
        )}
        {openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL && (
          <DocumentScreenshotViewerModal
            title={DOCUMENT_SCREENSHOT_VIEWER_TITLE}
          />
        )}
      </>
    </div>
  );
};

export default MedicalPaOrders;
